import { useEffect, useState } from "react";
import { AccessibilityInfo, Platform } from "react-native";

import { useDevSession } from "../dev/devSession";

const QUERY = "(prefers-reduced-motion: reduce)";

function webMedia(): MediaQueryList | null {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  return typeof window.matchMedia === "function"
    ? window.matchMedia(QUERY)
    : null;
}

/**
 * The OS "reduce motion" setting, live: prefers-reduced-motion on web,
 * AccessibilityInfo on native. The dev panel can force it on or off.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => webMedia()?.matches ?? false);

  useEffect(() => {
    const mq = webMedia();
    if (mq) {
      const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
      setReduced(mq.matches);
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
    }
    let alive = true;
    // A mocked or partial AccessibilityInfo may answer with nothing.
    AccessibilityInfo.isReduceMotionEnabled?.()
      ?.then((v) => alive && setReduced(v))
      .catch(() => undefined);
    const sub = AccessibilityInfo.addEventListener?.(
      "reduceMotionChanged",
      (v: boolean) => setReduced(v),
    );
    return () => {
      alive = false;
      sub?.remove?.();
    };
  }, []);

  const forced = useDevSession((s) => s.forceReducedMotion);
  return forced ?? reduced;
}
